// ============================================================
// VCS.InterpretRoom -- Dolmetscher mit Partner-Geraet, Messenger-Seite
// (2026-10-16)
// ============================================================
// Der Messenger ist hier entweder
//   - Gastgeber: laedt aus der Live-Unterhaltung heraus ein (Link, QR-Code,
//     Kontakt) und laesst anklopfende Gaeste herein, oder
//   - eingeladener Kontakt: bekommt "interpret-invited" und tritt bei.
// Die Sitzung selbst (Ansicht, Mikro, Text, Ton) ist VCS.InterpretSession,
// dieselbe wie auf der Gast-Seite.
//
// Server: signaling/modules/interpret-room.js. Nachrichten "interpret-*"
// leitet signaling.js hierher.
// ============================================================
window.VCS = window.VCS || {};

VCS.InterpretRoom = class {

    constructor() {
        this._session   = null;
        this._link      = null;     // { url, token, mode } des aktuellen Raums (Gastgeber)
        this._creating  = false;
        this._ringEl    = null;     // offener Klingel-Dialog
        this._ringKey   = null;
        this._ringTimer = 0;
    }

    init() {
        this._session = new VCS.InterpretSession(
            {
                send      : (obj) => VCS.signaling?.send(obj),
                sendBinary: (buf) => VCS.signaling?.sendBinary(buf),
            },
            {
                onEnded        : () => this._onSessionEnded(),
                onInviteLink   : () => this._shareLink(),
                onInviteQr     : () => this._session.toggleQr(this._link?.url),
                onInviteContact: () => this._openContactPicker(),
            }
        );
        this._injectInviteButton();
    }

    get active() {
        return !!this._session?.active;
    }

    // ============================================================
    //  Gastgeber: einladen
    // ============================================================

    // Knopf "Partner einladen" in der Live-Unterhaltung. Nur mit Recht.
    _injectInviteButton() {
        const toggles = document.querySelector('#liveConvoOverlay .live-convo-toggles');
        if (!toggles || document.getElementById('liveConvoInvitePartner')) {
            return;
        }
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.id = 'liveConvoInvitePartner';
        btn.className = 'live-convo-toggle live-convo-toggle--invite hidden';
        btn.innerHTML = '<span data-i18n="interpretPartnerBtn"></span>';
        btn.querySelector('span').textContent = t('interpretPartnerBtn');
        btn.addEventListener('click', () => void this._chooseMode());
        toggles.appendChild(btn);
        this.renderInviteButton();
    }

    renderInviteButton() {
        document.getElementById('liveConvoInvitePartner')
            ?.classList.toggle('hidden', !VCS.state.liveConvoRemote);
    }

    // Gespraech (ein Partner) oder Gruppe?
    async _chooseMode() {
        if (this.active || this._creating) {
            return;
        }
        document.querySelector('.interpret-choose-overlay')?.remove();
        const overlay = document.createElement('div');
        overlay.className = 'invite-sheet-overlay interpret-choose-overlay';
        overlay.innerHTML = `
            <div class="invite-sheet" role="dialog" aria-modal="true">
                <div class="invite-sheet-title">${escapeHtml(t('interpretChooseTitle'))}</div>
                <button type="button" class="interpret-choose-btn" data-mode="pair">
                    <span class="interpret-choose-label">${escapeHtml(t('interpretChoosePair'))}</span>
                    <span class="interpret-choose-sub">${escapeHtml(t('interpretChoosePairSub'))}</span>
                </button>
                <button type="button" class="interpret-choose-btn" data-mode="group">
                    <span class="interpret-choose-label">${escapeHtml(t('interpretChooseGroup'))}</span>
                    <span class="interpret-choose-sub">${escapeHtml(t('interpretChooseGroupSub'))}</span>
                </button>
                <div class="invite-sheet-actions">
                    <button type="button" class="invite-sheet-btn invite-sheet-btn--close" data-action="close">${escapeHtml(t('close'))}</button>
                </div>
            </div>`;
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay || e.target.closest('[data-action="close"]')) {
                overlay.remove();
                return;
            }
            const btn = e.target.closest('[data-mode]');
            if (btn) {
                overlay.remove();
                void this.startHost(btn.dataset.mode);
            }
        });
        document.body.appendChild(overlay);
    }

    async startHost(mode) {
        if (this._creating || this.active) {
            return;
        }
        // Solo-Sitzung laeuft? Erst beenden -- ein Mikro gehoert immer genau
        // einer Sitzung.
        if (VCS.liveConvo?._running) {
            const ok = await VCS.ui?.showConfirm?.(t('liveTalkConfirmEnd'));
            if (!ok) {
                return;
            }
            VCS.liveConvo._stopSession('partner');
        }
        this._creating = true;
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/invite/create', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({ 'Content-Type': 'application/json' }),
                body   : JSON.stringify({ kind: 'interpret', mode }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.url || !data.roomId) {
                console.warn('[interpret-room] create failed status=' + res.status + ' error=' + (data.error || '-'));
                const key = data.error === 'not_allowed' ? 'interpretNotAllowed'
                    : data.error === 'too_many_active_invites' ? 'inviteTooMany' : 'interpretCreateFailed';
                VCS.ui?.showToast?.(escapeHtml(t(key)), 'error', 5000);
                return;
            }
            this._link = { url: data.url, token: data.token, mode: data.mode };
            VCS.liveConvo?._closeOverlay?.();
            const lang = this._myLang();
            this._session.open({ roomId: data.roomId, mode: data.mode, role: 'host', lang });
            VCS.signaling?.send({ type: 'interpret-host', roomId: data.roomId, lang, name: this._myName() });
            // Am Tisch der schnellste Weg: QR-Code gleich zeigen.
            this._session.showQr(data.url);
            console.info('[interpret-room] Raum erstellt room=' + data.roomId + ' mode=' + data.mode);
        } catch (err) {
            console.warn('[interpret-room] create error: ' + (err?.message || 'unknown'));
            VCS.ui?.showToast?.(escapeHtml(t('interpretCreateFailed')), 'error', 5000);
        } finally {
            this._creating = false;
        }
    }

    _shareLink() {
        if (!this._link) {
            return;
        }
        const group = this._link.mode === 'group';
        VCS.chatContacts?._showInviteSheet?.(this._link.url, this._link.token, {
            title     : t('interpretInviteSheetTitle'),
            shareText : t('interpretInviteShareText'),
            shareTitle: t('interpretInviteShareTitle'),
            note      : t(group ? 'interpretInviteNoteGroup' : 'interpretInviteNote'),
        });
    }

    // Bernoria-Kontakt direkt einladen (Push). Auswahl aus den 1:1-Chats --
    // der Server prueft, ob es ein bestaetigter Kontakt ist.
    _openContactPicker() {
        document.querySelector('.interpret-contact-overlay')?.remove();
        const botPhone = VCS.Chat?.BOT_PHONE || '';
        const contacts = (VCS.state.chatChats || [])
            .filter(c => !c.isGroup && !c._isGroup && !c.isDatingMatch)
            .map(c => ({ phone: VCS.chat?.peerPhone?.(c) || '', name: c._peerName || '' }))
            .filter(c => c.phone && c.phone !== botPhone)
            .sort((a, b) => (a.name || a.phone).localeCompare(b.name || b.phone));

        const rows = contacts.length
            ? contacts.map(c => `
                <button type="button" class="interpret-contact-row" data-phone="${escapeHtml(c.phone)}" data-name="${escapeHtml(c.name || c.phone)}">
                    <span class="interpret-contact-avatar">${escapeHtml((c.name || '?').charAt(0).toUpperCase())}</span>
                    <span class="interpret-contact-name">${escapeHtml(c.name || c.phone)}</span>
                </button>`).join('')
            : `<p class="invite-link-hint">${escapeHtml(t('interpretContactEmpty'))}</p>`;

        const overlay = document.createElement('div');
        overlay.className = 'invite-sheet-overlay interpret-contact-overlay';
        overlay.innerHTML = `
            <div class="invite-sheet" role="dialog" aria-modal="true">
                <div class="invite-sheet-title">${escapeHtml(t('interpretContactTitle'))}</div>
                <div class="interpret-contact-list">${rows}</div>
                <div class="invite-sheet-actions">
                    <button type="button" class="invite-sheet-btn invite-sheet-btn--close" data-action="close">${escapeHtml(t('close'))}</button>
                </div>
            </div>`;
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay || e.target.closest('[data-action="close"]')) {
                overlay.remove();
                return;
            }
            const row = e.target.closest('[data-phone]');
            if (row) {
                overlay.remove();
                this._inviteNames = this._inviteNames || new Map();
                this._inviteNames.set(row.dataset.phone, row.dataset.name);
                VCS.signaling?.send({ type: 'interpret-invite', phone: row.dataset.phone });
            }
        });
        document.body.appendChild(overlay);
    }

    // ============================================================
    //  Nachrichten (aus signaling.js)
    // ============================================================

    onMessage(msg) {
        switch (msg.type) {
            case 'interpret-knock':
                this._onKnock(msg);
                return;
            case 'interpret-knock-cancelled':
                this._closeRing('knock:' + msg.roomId + ':' + (msg.guestId || ''));
                if (!msg.guestId) this._closeRingForRoom(msg.roomId);
                return;
            case 'interpret-invited':
                this._onInvited(msg);
                return;
            case 'interpret-invite-cancelled':
                this._closeRingForRoom(msg.roomId);
                return;
            case 'interpret-invite-state':
                this._onInviteState(msg);
                return;
            default:
                this._session?.handle(msg);
        }
    }

    onChatAuthed() {
        this.renderInviteButton();
        // Nach einem Verbindungsabbruch den Raum wieder betreten.
        if (!this.active) {
            return;
        }
        const roomId = this._session.roomId;
        const lang = this._myLang();
        if (this._session.role === 'host') {
            VCS.signaling?.send({ type: 'interpret-host', roomId, lang, name: this._myName() });
        } else {
            VCS.signaling?.send({ type: 'interpret-join', roomId, lang });
        }
        console.info('[interpret-room] wieder verbunden, Raum neu betreten room=' + roomId);
    }

    onConnectionLost() {
        this._session?.onConnectionLost();
    }

    _onSessionEnded() {
        this._link = null;
    }

    // -- Gast klopft an -------------------------------------------

    _onKnock(msg) {
        const roomId = typeof msg.roomId === 'string' ? msg.roomId : '';
        if (!roomId || !msg.guestId) {
            return;
        }
        // Schon in einer anderen Sitzung: nicht dazwischenfunken.
        if (this.active && this._session.roomId !== roomId) {
            VCS.ui?.showToast?.(escapeHtml(t('interpretKnockBusy').replace('{name}', msg.guestName || t('guest'))), 'info', 6000);
            return;
        }
        const name = String(msg.guestName || '').trim() || t('guest');
        const langName = (typeof LANG_NAMES !== 'undefined' && LANG_NAMES[msg.guestLang]) || '';
        this._ring({
            key     : 'knock:' + roomId + ':' + msg.guestId,
            roomId,
            name,
            typeText: t('interpretKnockType') + (langName ? ' · ' + langName : ''),
            onAccept: () => {
                if (!this.active) {
                    const lang = this._myLang();
                    const mode = roomId.startsWith('interp-g-') ? 'group' : 'pair';
                    this._session.open({ roomId, mode, role: 'host', lang });
                }
                VCS.signaling?.send({ type: 'interpret-accept', roomId, guestId: msg.guestId, lang: this._myLang(), name: this._myName() });
            },
            onDecline: () => VCS.signaling?.send({ type: 'interpret-deny', roomId, guestId: msg.guestId }),
        });
    }

    // -- Kontakt wird eingeladen ----------------------------------

    _onInvited(msg) {
        const roomId = typeof msg.roomId === 'string' ? msg.roomId : '';
        if (!roomId || this.active) {
            return;
        }
        const name = String(msg.hostName || '').trim() || t('guest');
        this._ring({
            key     : 'invite:' + roomId,
            roomId,
            name,
            typeText: t('interpretInvitedType'),
            onAccept: () => {
                const lang = VCS.state.currentLang || 'de';
                this._session.open({ roomId, mode: msg.mode, role: 'user', lang, hostName: name });
                VCS.signaling?.send({ type: 'interpret-join', roomId, lang });
            },
            onDecline: () => VCS.signaling?.send({ type: 'interpret-decline', roomId }),
        });
    }

    _onInviteState(msg) {
        const name = this._inviteNames?.get(msg.phone) || '';
        const key = {
            ringing    : 'interpretInviteRinging',
            accepted   : 'interpretInviteAccepted',
            declined   : 'interpretInviteDeclined',
            timeout    : 'interpretInviteTimeout',
            not_contact: 'interpretInviteNotContact',
            full       : 'interpretInviteFull',
        }[msg.state];
        if (!key) {
            return;
        }
        const kind = (msg.state === 'declined' || msg.state === 'timeout' || msg.state === 'not_contact' || msg.state === 'full') ? 'error' : 'info';
        VCS.ui?.showToast?.(escapeHtml(t(key).replace('{name}', name || t('guest'))), kind, 5000);
    }

    // -- Klingel-Dialog (wie beim Gast-Anruf) ---------------------

    _ring({ key, roomId, name, typeText, onAccept, onDecline }) {
        this._closeRing();
        const overlay = document.createElement('div');
        overlay.className = 'incoming-call-overlay';
        overlay.dataset.interpretRoom = roomId;
        overlay.innerHTML = `
            <div class="incoming-call-avatar">${escapeHtml(name.charAt(0).toUpperCase())}</div>
            <div class="incoming-call-name">${escapeHtml(name)}</div>
            <div class="incoming-call-type">${escapeHtml(typeText)}</div>
            <div class="incoming-call-actions">
                <button class="incoming-call-btn incoming-call-btn--decline" data-action="decline">
                    <span class="incoming-call-btn-circle">
                        <svg viewBox="0 0 24 24"><path d="M6.34 17.66l-1.41-1.41L10.59 12 4.93 6.34l1.41-1.41L12 10.59l5.66-5.66 1.41 1.41L13.41 12l5.66 5.66-1.41 1.41L12 13.41z"/></svg>
                    </span>
                    <span class="incoming-call-btn-label">${escapeHtml(t('decline'))}</span>
                </button>
                <button class="incoming-call-btn incoming-call-btn--accept" data-action="accept">
                    <span class="incoming-call-btn-circle">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path></svg>
                    </span>
                    <span class="incoming-call-btn-label">${escapeHtml(t('accept'))}</span>
                </button>
            </div>`;
        overlay.querySelector('[data-action="accept"]').onclick = () => {
            this._closeRing();
            onAccept();
        };
        overlay.querySelector('[data-action="decline"]').onclick = () => {
            this._closeRing();
            onDecline();
        };
        document.body.appendChild(overlay);
        this._ringEl = overlay;
        this._ringKey = key;
        VCS.audioMonitor?.startIncomingKnock?.();
        VCS.notify?.maybeNotifyJoinRequest?.(name);
        // Der Server beendet das Klingeln nach LIVE_CONVO_RING_MS (90 s) ebenfalls.
        this._ringTimer = setTimeout(() => this._closeRing(), 90000);
    }

    _closeRing(key) {
        if (key && this._ringKey && key !== this._ringKey) {
            return;
        }
        clearTimeout(this._ringTimer);
        this._ringTimer = 0;
        if (this._ringEl) {
            VCS.audioMonitor?.stopIncomingKnock?.();
            this._ringEl.remove();
        }
        this._ringEl = null;
        this._ringKey = null;
    }

    _closeRingForRoom(roomId) {
        if (this._ringEl && this._ringEl.dataset.interpretRoom === roomId) {
            this._closeRing();
        }
    }

    // -- Helfer ---------------------------------------------------

    // Eigene Sprache = Sprache 1 der Live-Unterhaltung, sonst die Oberflaeche.
    _myLang() {
        return VCS.liveConvo?._langA || VCS.state.currentLang || 'de';
    }

    _myName() {
        return VCS.state.chatMyName || VCS.state.myName || '';
    }
};

// Singleton, beim Bundle-Init angehaengt (analog VCS.liveConvo).
VCS.interpretRoom = new VCS.InterpretRoom();
